/* eslint-disable react/no-unescaped-entities */
/* Assistant « Nouvelle location » — Wizard 4 étapes.
 *
 * Étape 1 : Sélection du bien
 * Étape 2 : Sélection du lot
 * Étape 3 : Sélection ou création du locataire
 * Étape 4 : Type de bail + conditions financières + génération
 *
 * Le wizard interroge :
 *   POST /api/lease-wizard/prefill   → données auto-remplies
 *   POST /api/lease-wizard/validate  → contrôles juridiques
 *   POST /api/lease-wizard/finalize  → création du bail + PDF
 *
 * Pensé pour la Phase 2 (signature) : les données de génération sont
 * stockées dans un state unique, prêt à être sérialisé.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Home, Users, FileSignature, CheckCircle2, ChevronRight, ChevronLeft,
  AlertTriangle, Building2, Sparkles, Download, ArrowRight,
} from "lucide-react";

const STEP_TITLES = [
  { id: "building", label: "Bien", icon: Building2 },
  { id: "lot",      label: "Lot",  icon: Home },
  { id: "tenant",   label: "Locataire", icon: Users },
  { id: "lease",    label: "Bail", icon: FileSignature },
];

const LEASE_TYPES = [
  { code: "residential_unfurnished", label: "Habitation vide",           template: "habitation_vide" },
  { code: "residential_furnished",   label: "Habitation meublée",        template: "habitation_meublee" },
  { code: "mobility",                label: "Bail mobilité (1 à 10 mois)", template: "mobilite" },
  { code: "seasonal",                label: "Location saisonnière",       template: "saisonnier" },
  { code: "professional",            label: "Bail professionnel (libéral)", template: "professionnel" },
  { code: "commercial",              label: "Bail commercial 3-6-9",       template: "commercial" },
  { code: "derogatory",              label: "Bail dérogatoire (courte durée, ≤ 36 mois)", template: "derogatoire" },
];

// ---------------------------------------------------------------------------
// Persistance du brouillon dans localStorage.
// Sauvegarde: form + selection + step à chaque changement.
// Restauration: au montage du composant.
// Effacement: après finalize réussi (bail créé) OU sur bouton "Repartir de zéro".
// ---------------------------------------------------------------------------
const DRAFT_STORAGE_KEY = "fonbex.lease_wizard.draft.v1";

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Sanity check — on ignore un brouillon corrompu.
    if (typeof parsed !== "object" || !parsed.form || !parsed.selection) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
}

function saveDraft(state) {
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(state));
  } catch (_e) { /* quota exceeded — ignoré */ }
}

function clearDraft() {
  try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (_e) { /* noop */ }
}

export default function LeaseWizardPage() {
  const navigate = useNavigate();

  // Restauration du brouillon avant le premier render (initializer lazy).
  const initialDraft = loadDraft();

  const [step, setStep] = useState(initialDraft?.step ?? 0);
  const [buildings, setBuildings] = useState([]);
  const [lots, setLots] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selection, setSelection] = useState(initialDraft?.selection || {
    building_id: "", lot_id: "", tenant_ids: [], lease_type: "residential_unfurnished",
  });
  const [prefill, setPrefill] = useState(null);
  const [form, setForm] = useState(initialDraft?.form || {
    template_code: "habitation_vide",
    start_date: new Date().toISOString().slice(0, 10),
    duration_months: 36,
    rent_hc: "",
    provisions_charges: "",
    deposit_amount: "",
    payment_day: 5,
    // Qualité juridique du bailleur — pilote la durée minimale du bail
    // d'habitation vide. Valeurs :
    //   "" (défaut)           → inférence automatique côté backend
    //   "natural_person"      → personne physique
    //   "sci_familiale"       → SCI familiale confirmée (radio "Oui")
    //   "sci_unknown"         → radio "Je ne sais pas" → bloque proprement
    //   "legal_entity_other"  → autre personne morale / SCI non familiale
    lessor_kind: "",
  });
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Auto-save du brouillon dès qu'un champ ou une sélection change.
  useEffect(() => {
    saveDraft({ step, selection, form });
  }, [step, selection, form]);

  useEffect(() => {
    api.get("/buildings").then(({ data }) => setBuildings(data.items || []));
    api.get("/tenants").then(({ data }) => setTenants(data.items || []));
  }, []);

  useEffect(() => {
    if (selection.building_id) {
      api.get("/lots").then(({ data }) => {
        setLots((data.items || []).filter((l) => l.building_id === selection.building_id));
      });
    } else setLots([]);
  }, [selection.building_id]);

  const canNext = useMemo(() => {
    if (step === 0) return !!selection.building_id;
    if (step === 1) return !!selection.lot_id;
    if (step === 2) return selection.tenant_ids.length > 0;
    return true;
  }, [step, selection]);

  const goNext = async () => {
    if (step === 2) {
      // Chargement du prefill avant l'étape 4
      setBusy(true);
      try {
        const { data } = await api.post("/lease-wizard/prefill", {
          lot_id: selection.lot_id,
          tenant_ids: selection.tenant_ids,
          lease_type: selection.lease_type,
        });
        setPrefill(data);
        const s = data.suggested_lease || {};
        setForm((f) => ({
          ...f,
          template_code: data.template?.code || "habitation_vide",
          duration_months: data.template?.default_duration_months || 36,
          rent_hc: s.rent_hc || "",
          provisions_charges: s.provisions_charges || 0,
          deposit_amount: s.deposit_amount || "",
          start_date: s.start_date || f.start_date,
        }));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Chargement des données impossible");
        return;
      } finally { setBusy(false); }
    }
    setStep((s) => Math.min(3, s + 1));
    setValidation(null);
  };

  const goPrev = () => { setStep((s) => Math.max(0, s - 1)); setValidation(null); };

  const runValidation = async () => {
    setBusy(true);
    try {
      // Résolution du lessor_kind : si SCI détectée dans le prefill et bail
      // habitation vide, on transmet la valeur choisie par l'utilisateur.
      // Sinon, on n'envoie rien → inférence backend (personne physique par
      // défaut, autre personne morale via legal_form).
      const isVide = form.template_code === "habitation_vide";
      const isSCI = (prefill?.company?.legal_form || "").toUpperCase().startsWith("SCI");
      let lessor_kind = null;
      if (isVide && isSCI) {
        if (!form.lessor_kind) {
          toast.error("Veuillez préciser si la SCI est familiale (question ci-dessus).");
          setBusy(false);
          return;
        }
        lessor_kind = form.lessor_kind;
      } else if (form.lessor_kind && form.lessor_kind !== "sci_unknown") {
        lessor_kind = form.lessor_kind;
      }

      const { data } = await api.post("/lease-wizard/validate", {
        lot_id: selection.lot_id,
        tenant_ids: selection.tenant_ids,
        template_code: form.template_code,
        start_date: form.start_date,
        duration_months: parseInt(form.duration_months, 10),
        rent_hc: parseFloat(form.rent_hc || 0),
        provisions_charges: parseFloat(form.provisions_charges || 0),
        deposit_amount: parseFloat(form.deposit_amount || 0),
        payment_day: parseInt(form.payment_day, 10),
        ...(lessor_kind ? { lessor_kind } : {}),
      });
      setValidation(data);
      if (!data.ok) toast.error("Le dossier contient des blockers — vérifiez les alertes.");
      else toast.success("Dossier validé — prêt à générer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation impossible");
    } finally { setBusy(false); }
  };

  const finalize = async () => {
    if (!validation?.ok) { toast.error("Validez d'abord le dossier"); return; }
    setBusy(true);
    try {
      const isVide = form.template_code === "habitation_vide";
      const isSCI = (prefill?.company?.legal_form || "").toUpperCase().startsWith("SCI");
      let lessor_kind = null;
      if (isVide && isSCI && form.lessor_kind && form.lessor_kind !== "sci_unknown") {
        lessor_kind = form.lessor_kind;
      } else if (form.lessor_kind && form.lessor_kind !== "sci_unknown") {
        lessor_kind = form.lessor_kind;
      }

      const { data } = await api.post("/lease-wizard/finalize", {
        lot_id: selection.lot_id,
        tenant_ids: selection.tenant_ids,
        template_code: form.template_code,
        start_date: form.start_date,
        duration_months: parseInt(form.duration_months, 10),
        rent_hc: parseFloat(form.rent_hc || 0),
        provisions_charges: parseFloat(form.provisions_charges || 0),
        deposit_amount: parseFloat(form.deposit_amount || 0),
        payment_day: parseInt(form.payment_day, 10),
        lease_type: selection.lease_type,
        ...(lessor_kind ? { lessor_kind } : {}),
      });
      setResult(data);
      // Bail créé avec succès → effacer le brouillon pour laisser une
      // ardoise propre à la prochaine ouverture du wizard.
      clearDraft();
      toast.success("Location créée avec succès");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Génération impossible");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!result?.pdf_document_id) return;
    try {
      const { data } = await api.get(`/lease-wizard/documents/${result.pdf_document_id}`);
      const link = document.createElement("a");
      link.href = data.data_url;
      link.download = `bail-${result.lease_id.slice(0, 8)}.pdf`;
      link.click();
    } catch { toast.error("Téléchargement impossible"); }
  };

  if (result) {
    return (
      <div className="animate-fade-up max-w-3xl mx-auto" data-testid="wizard-result">
        <div className="card-elevated p-8 text-center">
          <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600 mb-4" />
          <h1 className="font-serif text-3xl mb-2">Location créée</h1>
          <p className="text-muted-foreground mb-6">
            Le bail est enregistré, les échéances sont générées, le lot est marqué occupé.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            {result.pdf_document_id && (
              <Button onClick={downloadPdf} data-testid="wizard-download-pdf">
                <Download className="mr-2 h-4 w-4" /> Télécharger le bail PDF
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/app/locations")}>
              Voir toutes les locations
            </Button>
          </div>
          {result.pdf_error && (
            <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 text-left">
              PDF non généré : {result.pdf_error}. Le bail a bien été créé et peut être régénéré depuis la fiche.
            </div>
          )}
          <div className="mt-8 text-left border-t border-border pt-6">
            <div className="text-overline mb-2">Prochaines étapes</div>
            <ul className="space-y-1 text-sm">
              {result.next_steps.map((s) => (
                <li key={s} className="flex items-start gap-2">
                  <ArrowRight className="h-3 w-3 mt-1 text-accent flex-shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-up" data-testid="lease-wizard-page">
      <PageHeader
        overline="Assistant"
        title="Nouvelle location"
        description="Créez un dossier locatif complet en quelques minutes — bien, lot, locataire, bail, PDF."
      />

      <Stepper current={step} />

      <div className="mt-8 card-elevated p-6 lg:p-8">
        {step === 0 && (
          <StepBuilding
            buildings={buildings}
            value={selection.building_id}
            onChange={(v) => setSelection({ ...selection, building_id: v, lot_id: "" })}
          />
        )}
        {step === 1 && (
          <StepLot
            lots={lots}
            value={selection.lot_id}
            onChange={(v) => setSelection({ ...selection, lot_id: v })}
          />
        )}
        {step === 2 && (
          <StepTenant
            tenants={tenants}
            selected={selection.tenant_ids}
            onChange={(ids) => setSelection({ ...selection, tenant_ids: ids })}
            leaseType={selection.lease_type}
            onLeaseType={(v) => {
              const tpl = LEASE_TYPES.find((t) => t.code === v)?.template || "habitation_vide";
              setSelection({ ...selection, lease_type: v });
              setForm((f) => ({ ...f, template_code: tpl }));
            }}
            onTenantCreated={(t) => setTenants((prev) => [...prev, t])}
          />
        )}
        {step === 3 && (
          <StepLease
            prefill={prefill}
            form={form}
            setForm={setForm}
            validation={validation}
          />
        )}

        <div className="mt-8 pt-6 border-t border-border flex justify-between items-center">
          <Button variant="ghost" onClick={goPrev} disabled={step === 0 || busy} data-testid="wizard-prev">
            <ChevronLeft className="mr-1 h-4 w-4" /> Précédent
          </Button>
          {step < 3 ? (
            <Button onClick={goNext} disabled={!canNext || busy} data-testid="wizard-next">
              Suivant <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <div className="flex gap-2">
              {!validation?.ok && (
                <Button variant="outline" onClick={runValidation} disabled={busy} data-testid="wizard-validate">
                  <CheckCircle2 className="mr-2 h-4 w-4" /> Contrôler
                </Button>
              )}
              {validation?.ok && (
                <Button onClick={finalize} disabled={busy} data-testid="wizard-finalize">
                  <Sparkles className="mr-2 h-4 w-4" /> Générer le dossier
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stepper({ current }) {
  return (
    <div className="mt-6 flex items-center gap-2 overflow-x-auto">
      {STEP_TITLES.map((s, i) => {
        const Icon = s.icon;
        const active = i === current;
        const done = i < current;
        return (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-2 flex-shrink-0 ${done ? "text-emerald-700" : active ? "text-accent" : "text-muted-foreground"}`}>
              <div className={`flex h-8 w-8 items-center justify-center rounded-full border ${done ? "bg-emerald-100 border-emerald-300" : active ? "bg-accent/10 border-accent" : "bg-secondary border-border"}`}>
                <Icon className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium">{s.label}</span>
            </div>
            {i < STEP_TITLES.length - 1 && (
              <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StepBuilding({ buildings, value, onChange }) {
  const [q, setQ] = useState("");
  const filtered = buildings.filter((b) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (b.name || "").toLowerCase().includes(s)
      || (b.address || "").toLowerCase().includes(s);
  });
  return (
    <div>
      <h2 className="font-heading text-xl mb-1">Choisissez le bien</h2>
      <p className="text-sm text-muted-foreground mb-4">Sélectionnez l&apos;immeuble concerné par la location.</p>
      {buildings.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          Aucun immeuble.
          <Button asChild variant="link" size="sm"><Link to="/app/buildings">Créer un immeuble</Link></Button>
        </div>
      ) : (
        <>
          {buildings.length > 4 && (
            <Input
              placeholder="Rechercher par nom ou adresse…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="mb-4"
              data-testid="wizard-building-search"
            />
          )}
          <div className="grid gap-3 md:grid-cols-2 max-h-96 overflow-y-auto pr-1">
            {filtered.length === 0 && (
              <div className="col-span-full text-center py-6 text-sm text-muted-foreground">
                Aucun immeuble ne correspond à « {q} ».
              </div>
            )}
            {filtered.map((b) => (
              <label
                key={b.id}
                className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer hover:border-accent transition-colors ${value === b.id ? "border-accent bg-accent/5" : "border-border"}`}
                data-testid={`wizard-building-${b.id}`}
              >
                <input type="radio" name="building" value={b.id} checked={value === b.id}
                       onChange={(e) => onChange(e.target.value)} className="mt-1" />
                <div>
                  <div className="font-medium">{b.name}</div>
                  <div className="text-xs text-muted-foreground">{b.address}</div>
                </div>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StepLot({ lots, value, onChange }) {
  const [q, setQ] = useState("");
  const filtered = lots.filter((l) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (l.reference || "").toLowerCase().includes(s)
      || (l.lot_type || "").toLowerCase().includes(s);
  });
  return (
    <div>
      <h2 className="font-heading text-xl mb-1">Choisissez le lot</h2>
      <p className="text-sm text-muted-foreground mb-4">Sélectionnez le lot précis à louer.</p>
      {lots.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          Aucun lot pour cet immeuble.
          <Button asChild variant="link" size="sm"><Link to="/app/buildings">Créer un lot</Link></Button>
        </div>
      ) : (
        <>
          {lots.length > 4 && (
            <Input
              placeholder="Rechercher par référence ou type…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="mb-4"
              data-testid="wizard-lot-search"
            />
          )}
          <div className="grid gap-3 md:grid-cols-2 max-h-96 overflow-y-auto pr-1">
            {filtered.length === 0 && (
              <div className="col-span-full text-center py-6 text-sm text-muted-foreground">
                Aucun lot ne correspond à « {q} ».
              </div>
            )}
            {filtered.map((l) => (
              <label
                key={l.id}
                className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer hover:border-accent transition-colors ${value === l.id ? "border-accent bg-accent/5" : "border-border"}`}
                data-testid={`wizard-lot-${l.id}`}
              >
                <input type="radio" name="lot" value={l.id} checked={value === l.id}
                       onChange={(e) => onChange(e.target.value)} className="mt-1" />
                <div className="flex-1">
                  <div className="font-medium">{l.reference}</div>
                  <div className="text-xs text-muted-foreground">
                    {l.surface_m2 && `${l.surface_m2} m²`}
                    {l.rooms && ` · ${l.rooms} pièce${l.rooms > 1 ? "s" : ""}`}
                    {l.occupancy_status === "occupied" && " · Actuellement occupé"}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StepTenant({ tenants, selected, onChange, leaseType, onLeaseType, onTenantCreated }) {
  const [q, setQ] = useState("");
  const [addingOpen, setAddingOpen] = useState(false);
  const [newT, setNewT] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);

  const filtered = tenants.filter((t) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    const name = (t.company_name || `${t.first_name || ""} ${t.last_name || ""}`).toLowerCase();
    return name.includes(s) || (t.email || "").toLowerCase().includes(s);
  });

  const createTenant = async () => {
    if (!newT.first_name.trim() && !newT.last_name.trim()) {
      toast.error("Nom ou prénom requis"); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/tenants", {
        kind: "individual",
        first_name: newT.first_name.trim() || null,
        last_name: newT.last_name.trim() || null,
        email: newT.email.trim() || null,
        phone: newT.phone.trim() || null,
      });
      toast.success("Locataire créé");
      onTenantCreated?.(data);
      onChange([...selected, data.id]); // pré-sélectionner
      setAddingOpen(false);
      setNewT({ first_name: "", last_name: "", email: "", phone: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Création impossible");
    } finally { setSaving(false); }
  };

  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div>
      <h2 className="font-heading text-xl mb-1">Locataires et type de bail</h2>
      <p className="text-sm text-muted-foreground mb-6">Sélectionnez le(s) locataire(s) et le type de location.</p>

      <div className="mb-6">
        <Label className="text-xs mb-2 block">Type de location</Label>
        <div className="grid gap-2 md:grid-cols-2">
          {LEASE_TYPES.map((t) => (
            <label key={t.code}
              className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer hover:border-accent ${leaseType === t.code ? "border-accent bg-accent/5" : "border-border"}`}>
              <input type="radio" name="type" value={t.code} checked={leaseType === t.code}
                     onChange={(e) => onLeaseType(e.target.value)} data-testid={`wizard-type-${t.code}`} />
              <span className="text-sm">{t.label}</span>
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          7 types de baux disponibles — la clause résolutoire, la sous-location, la colocation et 15 autres clauses optionnelles seront proposées à l&apos;étape suivante.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between gap-3 mb-2">
          <Label className="text-xs">Locataires ({selected.length} sélectionné{selected.length > 1 ? "s" : ""})</Label>
          <Button type="button" size="sm" variant="outline"
                  onClick={() => setAddingOpen((o) => !o)}
                  data-testid="wizard-add-tenant-btn">
            {addingOpen ? "Fermer" : "+ Créer un locataire"}
          </Button>
        </div>

        {addingOpen && (
          <div className="mb-4 rounded-xl border border-accent/30 bg-accent/5 p-4 space-y-3"
               data-testid="wizard-add-tenant-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input placeholder="Prénom" value={newT.first_name}
                     onChange={(e) => setNewT({ ...newT, first_name: e.target.value })}
                     data-testid="wizard-new-tenant-first-name" />
              <Input placeholder="Nom" value={newT.last_name}
                     onChange={(e) => setNewT({ ...newT, last_name: e.target.value })}
                     data-testid="wizard-new-tenant-last-name" />
              <Input placeholder="Email (facultatif)" type="email" value={newT.email}
                     onChange={(e) => setNewT({ ...newT, email: e.target.value })} />
              <Input placeholder="Téléphone (facultatif)" value={newT.phone}
                     onChange={(e) => setNewT({ ...newT, phone: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setAddingOpen(false)}>Annuler</Button>
              <Button type="button" size="sm" onClick={createTenant} disabled={saving}
                      data-testid="wizard-new-tenant-save">
                {saving ? "Création…" : "Créer et sélectionner"}
              </Button>
            </div>
          </div>
        )}

        {tenants.length === 0 && !addingOpen ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Aucun locataire — cliquez sur « + Créer un locataire » pour en ajouter un.
          </div>
        ) : (
          <>
            {tenants.length > 4 && (
              <Input
                placeholder="Rechercher par nom ou email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="mb-3"
                data-testid="wizard-tenant-search"
              />
            )}
            <div className="grid gap-2 md:grid-cols-2 max-h-72 overflow-y-auto pr-1">
              {filtered.length === 0 && (
                <div className="col-span-full text-center py-6 text-sm text-muted-foreground">
                  Aucun locataire ne correspond à « {q} ».
                </div>
              )}
              {filtered.map((t) => (
                <label key={t.id}
                  className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer hover:border-accent ${selected.includes(t.id) ? "border-accent bg-accent/5" : "border-border"}`}
                  data-testid={`wizard-tenant-${t.id}`}>
                  <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggle(t.id)} />
                  <div>
                    <div className="font-medium text-sm">
                      {t.company_name || `${t.first_name || ""} ${t.last_name || ""}`.trim()}
                    </div>
                    <div className="text-xs text-muted-foreground">{t.email || "—"}</div>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StepLease({ prefill, form, setForm, validation }) {
  if (!prefill) return <div className="text-muted-foreground">Chargement du pré-remplissage…</div>;
  const t = prefill.template || {};
  const update = (k, v) => setForm({ ...form, [k]: v });

  // Détection SCI + habitation vide → question SCI familiale obligatoire.
  const legalForm = (prefill.company?.legal_form || "").toUpperCase();
  const isSCI = legalForm.startsWith("SCI");
  const isVide = form.template_code === "habitation_vide";
  const showSciQuestion = isVide && isSCI;
  const sciAnswered = ["sci_familiale", "legal_entity_other", "sci_unknown"].includes(form.lessor_kind);

  return (
    <div>
      <h2 className="font-heading text-xl mb-1">Bail — conditions financières</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Modèle : <strong>{t.name}</strong> · Version {t.version} · Dépôt max autorisé : <strong>{t.max_deposit_months} mois</strong> de loyer HC
      </p>

      {showSciQuestion && (
        <div className="mb-6 rounded-xl border border-indigo-300 bg-indigo-50 p-4"
             data-testid="wizard-sci-familiale-question">
          <div className="font-medium text-sm text-indigo-950 mb-2">
            Cette SCI est-elle une SCI familiale au sens de l'article 13 de la
            loi du 6 juillet 1989 ?
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" name="sci_familiale" className="mt-1"
                     data-testid="wizard-sci-yes"
                     checked={form.lessor_kind === "sci_familiale"}
                     onChange={() => update("lessor_kind", "sci_familiale")} />
              <span><strong>Oui</strong> — SCI constituée exclusivement entre
              parents et alliés jusqu'au 4<sup>e</sup> degré inclus
              <span className="ml-2 text-xs text-indigo-800">
                (durée minimale : 36 mois)
              </span></span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" name="sci_familiale" className="mt-1"
                     data-testid="wizard-sci-no"
                     checked={form.lessor_kind === "legal_entity_other"}
                     onChange={() => update("lessor_kind", "legal_entity_other")} />
              <span><strong>Non</strong>
              <span className="ml-2 text-xs text-indigo-800">
                (durée minimale : 72 mois)
              </span></span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" name="sci_familiale" className="mt-1"
                     data-testid="wizard-sci-dontknow"
                     checked={form.lessor_kind === "sci_unknown"}
                     onChange={() => update("lessor_kind", "sci_unknown")} />
              <span><strong>Je ne sais pas</strong></span>
            </label>
          </div>
          <p className="mt-3 text-xs text-indigo-800 leading-relaxed">
            Pour l'application de cette règle, une SCI familiale est une société
            civile constituée exclusivement entre parents et alliés jusqu'au
            quatrième degré inclus. En cas de doute, vérifiez les associés de
            la SCI.
          </p>
          {form.lessor_kind === "sci_unknown" && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900"
                 data-testid="wizard-sci-dontknow-help">
              La génération du bail est momentanément bloquée. Consultez la liste
              des associés de votre SCI (statuts, K-bis) : si tous sont parents
              ou alliés jusqu'au 4<sup>e</sup> degré, sélectionnez « Oui » ;
              sinon « Non ». Vous pouvez ensuite revenir modifier ce choix.
            </div>
          )}
          {!sciAnswered && (
            <div className="mt-3 rounded-lg bg-red-50 border border-red-300 p-3 text-xs text-red-900"
                 data-testid="wizard-sci-required">
              Cette réponse est requise avant validation du bail.
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Date de début *">
          <Input type="date" value={form.start_date} onChange={(e) => update("start_date", e.target.value)}
                 data-testid="wizard-start-date" />
        </Field>
        <Field label="Durée (mois) *">
          <Input type="number" min="1" max="180" value={form.duration_months}
                 onChange={(e) => update("duration_months", e.target.value)}
                 data-testid="wizard-duration" />
        </Field>
        <Field label="Loyer HC (€) *">
          <Input type="number" min="0" step="0.01" value={form.rent_hc}
                 onChange={(e) => update("rent_hc", e.target.value)} data-testid="wizard-rent-hc" />
        </Field>
        <Field label="Provisions charges (€) /mois">
          <Input type="number" min="0" step="0.01" value={form.provisions_charges}
                 onChange={(e) => update("provisions_charges", e.target.value)}
                 data-testid="wizard-provisions" />
        </Field>
        <Field label={`Dépôt de garantie (€) — max ${t.max_deposit_months} mois HC`}>
          <Input type="number" min="0" step="0.01" value={form.deposit_amount}
                 onChange={(e) => update("deposit_amount", e.target.value)}
                 data-testid="wizard-deposit" />
        </Field>
        <Field label="Jour de paiement *">
          <Input type="number" min="1" max="28" value={form.payment_day}
                 onChange={(e) => update("payment_day", e.target.value)} />
        </Field>
      </div>

      {validation && (
        <div className="mt-6 space-y-2" data-testid="wizard-validation">
          {validation.blockers?.length > 0 && (
            <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-900">
              <div className="flex items-center gap-2 font-medium mb-1">
                <AlertTriangle className="h-4 w-4" /> Anomalies bloquantes
              </div>
              <ul className="text-xs space-y-0.5 ml-6 list-disc">
                {validation.blockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          )}
          {validation.warnings?.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="flex items-center gap-2 font-medium mb-1">
                <AlertTriangle className="h-4 w-4" /> Points d&apos;attention
              </div>
              <ul className="text-xs space-y-0.5 ml-6 list-disc">
                {validation.warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            </div>
          )}
          {validation.ok && validation.warnings?.length === 0 && (
            <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Le dossier est prêt à être généré.
            </div>
          )}
          {validation.ok && (validation.warnings?.length ?? 0) > 0 && (
            <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Génération possible malgré les points d&apos;attention.
            </div>
          )}
        </div>
      )}

      <div className="mt-6 rounded-xl bg-secondary/30 p-4 text-xs text-muted-foreground">
        <div className="font-medium mb-1 text-foreground">Annexes obligatoires</div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
          {(t.required_annexes || []).map((a) => <li key={a}>• {a}</li>)}
        </ul>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
